import {Box, Flex, useStyleConfig, HStack, IconButton} from '@chakra-ui/react'
import React, {Fragment, useState} from 'react'
import {BRAND_KEY} from '../../brand'
import {ChevronLeft, ChevronRight, RightIcon, LeftIcon, VerticalLineIcon} from '../icons'
import _ from 'lodash'
import CustomLink from '../custom-link'
import {useGetSovraHeader} from '../../custom-api/hooks/use-get-sovra-header'
import {useIntl} from 'react-intl'
import {safeBuildUrl} from '../../utils/contentstack-utils'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {remCalc} from '../../theme/_utils'

const SovraHeader = () => {
    const isBrandBraun = BRAND_KEY === 'braun'
    const linkVariant = isBrandBraun ? 'icon-button' : 'inverse-icon-button'

    const styles = useStyleConfig('SovraHeader')
    const [currentIndex, setCurrentIndex] = useState(0)
    const intl = useIntl()
    const {buildUrl} = useMultiSite()

    // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
    const {data, isFetching, isLoading, isSuccess, isError} = useGetSovraHeader()

    const groupLinkSchema = data?.grouplink

    // Do not render the component if there is no data
    if (!groupLinkSchema || groupLinkSchema.length === 0) {
        return null
    }

    // Change the current button index, only mobile view
    const switchButton = (direction) => {
        setCurrentIndex((prevIndex) =>
            direction === 'left'
                ? prevIndex === 0
                    ? groupLinkSchema?.length - 1
                    : prevIndex - 1
                : prevIndex === groupLinkSchema?.length - 1
                ? 0
                : prevIndex + 1,
        )
    }

    // Mobile constants
    const isExternalUrl = !!groupLinkSchema?.[currentIndex]?.is_external_url
    const href = groupLinkSchema?.[currentIndex]?.header_link?.href
    const title = groupLinkSchema?.[currentIndex]?.header_link?.title
    const isMobileLinkActive = !!href
    const mobileHref = isExternalUrl ? href ?? '' : safeBuildUrl(href, buildUrl)

    return (
        <Flex w="100%" pos="relative" zIndex={1200} id="sovra-header">
            {/* Mobile view*/}
            <Box {...styles.container} {...styles.containerMobile}>
                <Flex direction="row" w="100%" align="center" justify="space-between">
                    <IconButton
                        data-testid="left-arrow"
                        icon={
                            isBrandBraun ? (
                                <LeftIcon {...styles.iconArrow} />
                            ) : (
                                <ChevronLeft {...styles.iconArrow} />
                            )
                        }
                        onClick={() => switchButton('left')}
                        variant="unstyled"
                        aria-label={intl.formatMessage({
                            id: 'global.left-arrow.aria_label',
                            defaultMessage: 'Left icon',
                        })}
                    />
                    <CustomLink
                        asButton={isMobileLinkActive}
                        variant={linkVariant}
                        data-testid={`${_.kebabCase(title)}-current-button-mobile`}
                        isExternal={isExternalUrl && isMobileLinkActive}
                        href={mobileHref}
                        {...styles.labelSmall}
                        {...styles.labelColor}
                    >
                        {title}
                    </CustomLink>

                    {isBrandBraun ? (
                        <IconButton
                            data-testid="right-arrow"
                            variant="unstyled"
                            icon={<RightIcon {...styles.iconArrow} />}
                            onClick={() => switchButton('right')}
                            aria-label={intl.formatMessage({
                                id: 'global.right-arrow.aria_label',
                                defaultMessage: 'Right icon',
                            })}
                        />
                    ) : (
                        <IconButton
                            data-testid="right-arrow"
                            variant="unstyled"
                            icon={<ChevronRight {...styles.iconArrow} />}
                            onClick={() => switchButton('right')}
                            aria-label={intl.formatMessage({
                                id: 'global.right-arrow.aria_label',
                                defaultMessage: 'Right icon',
                            })}
                        />
                    )}
                </Flex>
            </Box>
            {/* Desktop view*/}
            <Box {...styles.container} {...styles.containerDesktop}>
                <HStack {...styles.hstack}>
                    {groupLinkSchema?.length > 0 &&
                        groupLinkSchema.map((button, index) => {
                            const isExternalUrl = !!button?.is_external_url
                            const href = button?.header_link?.href
                            const title = button?.header_link?.title
                            const isLinkActive = !!href
                            const desktopHref = isExternalUrl
                                ? href ?? ''
                                : safeBuildUrl(href, buildUrl)
                            return (
                                <Fragment key={index}>
                                    <CustomLink
                                        asButton={isLinkActive}
                                        variant={linkVariant}
                                        data-testid={`${_.kebabCase(title)}-current-button`}
                                        isExternal={isExternalUrl && isLinkActive}
                                        href={desktopHref}
                                        {...styles.label}
                                        {...styles.labelColor}
                                    >
                                        {title}
                                    </CustomLink>

                                    {index < groupLinkSchema.length - 1 &&
                                        (isBrandBraun ? (
                                            <Box
                                                w={remCalc(6)}
                                                h={remCalc(1)}
                                                bg="background.dark"
                                                opacity="0.5"
                                            />
                                        ) : (
                                            <VerticalLineIcon {...styles.icon} />
                                        ))}
                                </Fragment>
                            )
                        })}
                </HStack>
            </Box>
        </Flex>
    )
}

SovraHeader.getTemplateName = () => 'sovra-header'

export default SovraHeader
